/**
 * CANCEL A FREE ENROLMENT — no order, no payment, and therefore no ledger to read.
 *
 * WHY THIS ONE HAS A PREVIEW STEP AND THE PAID ONES DO NOT. A paid purchase leaves a grant
 * ledger: the server knows which access came from which order and can say so. A free enrolment
 * leaves almost nothing, so most attributions here are circumstantial — "the learner got this
 * course around the same time as that pathway" is a guess, not a record. The preview is not a
 * convenience screen in front of the real action; it IS the mechanism, because every unproven
 * revocation has to be confirmed by name before it happens.
 *
 * So the operator sees three groups, and they are three different kinds of fact:
 *
 *   will_revoke_automatically   proven from the grant record. Goes without asking.
 *   requires_confirmation       not proven. One checkbox each, unticked. Unticked stays.
 *   retained                    held independently of this enrolment. Not touched.
 *
 * THE LEARNER IS IDENTIFIED BY USERNAME AND/OR EMAIL, and the target by `pathway_key`. There is
 * no course key in the request — a member course is reached through its pathway, never named
 * directly. Neither identifier resolving is `409 learner_unresolvable`, which is a refusal about
 * the input and not an empty result.
 *
 * NOTHING HERE IS A REFUND. No payment was taken, so there is nothing to give back; this only
 * removes access. Stated in the dialog, from `money.explanation` plus `OPS_COPY.freeHasNoMoney`.
 */

import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Badge, Banner, Button, DataTable, Dialog, Spinner } from '../../shared/ui';
import type { Column } from '../../shared/ui';
import {
  cancelFreeEnrolment,
  explainOpsFailure,
  OPS_COPY,
  PAYMENT_OPS_KEY,
  previewFreeEnrolmentCancel,
  reasonProblem,
  type WireFreeCancelResult,
  type WireFreeDecision,
  type WireFreePreview,
} from '../api/paymentOps';
import { ReasonField } from './ReasonField';
import {
  CertificateChoices,
  CertificateResults,
  certificateChoicesComplete,
  OpsFailureNotice,
  opsAlreadyHappened,
  useOperationId,
  type CertificateChoiceMap,
} from './CertificateChoices';

/** Server vocabulary in operator English. An unknown value is rendered as itself, never blank. */
const VERDICT_LABEL: Record<string, string> = {
  revoke: 'Will be removed',
  retain_independently_owned: 'Stays — owned independently of this enrolment',
  retain_unproven: 'Stays unless you confirm it',
  retain_pre_existing: 'Stays — held before this enrolment',
  not_held: 'Not held, so there is nothing to remove',
};

const BASIS_LABEL: Record<string, string> = {
  recorded_at_grant: 'Recorded when the access was granted',
  inferred_temporal: 'Inferred from timing only',
  unknown: 'No evidence either way',
};

const label = (map: Record<string, string>, value: string): string =>
  map[value] ?? (value || 'The academy did not say');

function decisionColumns(): Column<WireFreeDecision>[] {
  return [
    {
      key: 'target',
      header: 'Item',
      mono: true,
      render: (row) => (
        <span>
          {row.target_key}
          <br />
          <small className="admin-muted">{row.target_kind || 'unknown kind'}</small>
        </span>
      ),
    },
    {
      key: 'verdict',
      header: 'What happens',
      render: (row) => (
        <Badge tone={row.verdict === 'revoke' ? 'danger' : 'success'}>
          {label(VERDICT_LABEL, row.verdict)}
        </Badge>
      ),
    },
    {
      key: 'proven',
      header: 'Proven?',
      render: (row) =>
        // `proven` and `basis` are two readings of one thing and both are shown, because
        // "inferred from timing" is the part an operator needs in order to disagree with it.
        row.proven ? (
          <Badge tone="success">Proven</Badge>
        ) : (
          <Badge tone="warning">Not proven</Badge>
        ),
    },
    { key: 'basis', header: 'On what evidence', render: (row) => label(BASIS_LABEL, row.basis) },
    { key: 'note', header: 'The academy’s note', render: (row) => row.note },
  ];
}

/**
 * The preview refusals that are NOT errors to shrug at, each with its own next step.
 *
 * `paid_enrolment_not_free` in particular means the operator is on the wrong screen: this
 * enrolment was paid for, so it has an order, and it belongs on the order screen where a refund
 * is possible. Rendering that as "something went wrong" would send somebody looking for a fault
 * that does not exist.
 */
function PreviewRefusal({ error }: { error: unknown }) {
  const failure = explainOpsFailure(error);

  if (failure.code === 'not_enrolled') {
    return (
      <Banner tone="info">
        <p>{failure.message}</p>
        <p>
          There is no enrolment here to cancel. Check the learner and the learning path — if they
          were removed already, the audit trail will show who did it and when.
        </p>
      </Banner>
    );
  }
  if (failure.code === 'paid_enrolment_not_free') {
    return (
      <Banner tone="warning">
        <p>{failure.message}</p>
        <p>
          <strong>This enrolment was paid for, so this is the wrong screen for it.</strong> It has
          an order, and the order screen is where money can be refunded as well as access removed.
          Cancelling it here would take the access away and leave the learner out of pocket.
        </p>
      </Banner>
    );
  }
  if (failure.code === 'learner_unresolvable') {
    return (
      <Banner tone="warning">
        <p>{failure.message}</p>
        <p>
          Neither the username nor the email matched anybody in this academy, so we do not know
          whose enrolment to look at. Check the spelling of both.
        </p>
      </Banner>
    );
  }
  return <OpsFailureNotice error={error} />;
}

export function FreeEnrolmentDialog({
  pathwayKey,
  /** Username and/or email. At least one is required; the server resolves the learner. */
  username,
  email,
  onClose,
  onDone,
}: {
  pathwayKey: string;
  username?: string;
  email?: string;
  onClose: () => void;
  /** Called when the operator dismisses the outcome. The parent owns the root query key. */
  onDone: (result: WireFreeCancelResult) => void;
}) {
  const [reason, setReason] = useState('');
  const [attempted, setAttempted] = useState(false);
  const [confirmed, setConfirmed] = useState<Record<string, boolean>>({});
  const [choices, setChoices] = useState<CertificateChoiceMap>({});
  const operation = useOperationId();

  const identified = Boolean(username?.trim() || email?.trim());

  const preview = useQuery<WireFreePreview>({
    queryKey: [...PAYMENT_OPS_KEY, 'free-preview', pathwayKey, username ?? '', email ?? ''],
    queryFn: () => previewFreeEnrolmentCancel({ pathwayKey, username, email }),
    enabled: identified,
    // A refusal here is a statement about the input, not a transient fault. Retrying it three
    // times delays the operator's answer and asks the server the same question twice more.
    retry: false,
    refetchOnWindowFocus: false,
  });

  const plan = preview.data;
  const requiresConfirmation = plan?.requires_confirmation ?? [];
  const offers = plan?.certificates ?? [];

  // FILTERED THROUGH THE CURRENT PREVIEW, so a tick left over from an earlier preview cannot be
  // sent. Sending a key the server no longer classifies as revocable is `409
  // confirmation_mismatch`, and the stale tick is the easiest way to cause it.
  const confirmKeys = requiresConfirmation.filter((key) => confirmed[key] === true);

  const cancel = useMutation({
    mutationFn: (operationId: string) =>
      cancelFreeEnrolment({
        operationId,
        pathwayKey,
        reason,
        username,
        email,
        confirmRevokeKeys: confirmKeys,
        certificateDecisions: choices,
      }),
  });

  const result = cancel.data;
  // `reason` IS required on the cancel route, unlike the preview, which takes none.
  const reasonIsUnusable = reasonProblem(reason) !== null;
  const certificatesAnswered = certificateChoicesComplete(offers, choices);
  const alreadyHappened = cancel.isError && opsAlreadyHappened(cancel.error);
  const staleConfirmation =
    cancel.isError && explainOpsFailure(cancel.error).code === 'confirmation_mismatch';

  const rerunPreview = () => {
    cancel.reset();
    // The ticks belonged to the plan being replaced. Keeping them across a re-run is how a
    // stale confirmation gets sent twice.
    setConfirmed({});
    void preview.refetch();
  };

  const submit = () => {
    setAttempted(true);
    if (operation.id === null || reasonIsUnusable || !certificatesAnswered) return;
    // Minted once when this dialog opened and reused on every retry, so a second press after a
    // failure is one operation to the server rather than two.
    cancel.mutate(operation.id);
  };

  const canSubmit = plan !== undefined && !alreadyHappened && !staleConfirmation;

  return (
    <Dialog
      title="Cancel this free enrolment?"
      wide
      onClose={onClose}
      footer={
        result ? (
          <Button variant="primary" onClick={() => onDone(result)}>
            Done
          </Button>
        ) : (
          <>
            {canSubmit && (
              <Button
                variant="danger"
                busy={cancel.isPending}
                disabled={operation.id === null || reasonIsUnusable || !certificatesAnswered}
                onClick={submit}
              >
                Remove access
              </Button>
            )}
            {staleConfirmation && (
              <Button variant="secondary" onClick={rerunPreview}>
                Run the preview again
              </Button>
            )}
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
          </>
        )
      }
    >
      {operation.problem !== null && <Banner tone="danger">{operation.problem}</Banner>}

      {!identified && (
        <Banner tone="warning">
          A learner has to be named by username or email before anything can be looked up. Neither
          was given, so there is nothing to preview — this is not an empty result.
        </Banner>
      )}

      {result ? (
        <>
          {/* A PARTIAL IS NOT A SUCCESS WITH A FOOTNOTE. `not_confirmed` non-empty means the
              audit outcome was `partially_succeeded`: some access was removed and some was
              deliberately left. Leading with "Cancelled" and burying this is exactly how
              somebody comes away believing an enrolment is fully cancelled when it is not. */}
          {result.not_confirmed.length > 0 ? (
            <Banner tone="warning">
              <p>
                <strong>Partly done — this enrolment is not fully cancelled.</strong> What you
                confirmed has been removed. {result.not_confirmed.length}{' '}
                {result.not_confirmed.length === 1 ? 'item was' : 'items were'} left in place
                because {result.not_confirmed.length === 1 ? 'it was' : 'they were'} not ticked.
              </p>
              <p>You can come back and remove them at any time.</p>
            </Banner>
          ) : (
            <Banner tone="success">
              Access removed. No money was involved in this enrolment, so nothing was refunded.
            </Banner>
          )}

          <h4>Removed</h4>
          {result.revoked.length === 0 ? (
            <p className="admin-muted">Nothing was removed.</p>
          ) : (
            <ul className="ops-results">
              {result.revoked.map((key) => (
                <li key={`revoked:${key}`}>
                  <code>{key}</code>
                </li>
              ))}
            </ul>
          )}

          {result.not_confirmed.length > 0 && (
            <>
              <h4>Left in place, because you did not confirm them</h4>
              <ul className="ops-results">
                {result.not_confirmed.map((key) => (
                  <li key={`not-confirmed:${key}`}>
                    <code>{key}</code> <Badge tone="warning">Still held</Badge>
                  </li>
                ))}
              </ul>
            </>
          )}

          <h4>Kept</h4>
          {result.retained.length === 0 ? (
            <p className="admin-muted">Nothing was held independently of this enrolment.</p>
          ) : (
            <ul className="ops-results">
              {/* THREE keys on this one — `target_key`, `decision`, `reason`. The preview's
                  `retained[]` has two and is a different type; the seam keeps them apart. */}
              {result.retained.map((row) => (
                <li key={`kept:${row.target_key}`}>
                  <code>{row.target_key}</code>{' '}
                  <Badge tone="success">{row.decision || 'kept'}</Badge>{' '}
                  <small className="admin-muted">{row.reason}</small>
                </li>
              ))}
            </ul>
          )}

          <h4>Certificates</h4>
          <CertificateResults results={result.certificates} />
        </>
      ) : (
        <>
          {preview.isPending && identified && <Spinner label="Working out what this would remove…" />}
          {preview.isError && <PreviewRefusal error={preview.error} />}

          {plan && (
            <>
              <Banner tone="info">
                <p>{OPS_COPY.freeHasNoMoney}</p>
                {/* The server's own sentence about the money, verbatim under ours. */}
                {plan.money.explanation ? <p>{plan.money.explanation}</p> : null}
                {plan.money.no_payment_taken === false && (
                  <p>
                    <strong>
                      The academy says a payment WAS taken for this enrolment, which contradicts
                      cancelling it here.
                    </strong>{' '}
                    Find its order and work from the order screen instead.
                  </p>
                )}
              </Banner>

              <p className="admin-muted">
                {plan.learner.username || plan.learner.email || 'This learner'} in{' '}
                <code>{plan.pathway_key}</code>
              </p>

              <h4>Everything this enrolment touches</h4>
              <DataTable
                columns={decisionColumns()}
                rows={plan.decisions}
                rowKey={(row) => `${row.target_kind}:${row.target_key}`}
                empty={
                  <>
                    <strong>The academy found nothing attached to this enrolment.</strong>
                    <p>
                      That is its answer, not a failure to look — there is nothing here to remove.
                    </p>
                  </>
                }
              />

              <h4>
                Will be removed without asking{' '}
                <Badge tone="danger">{plan.will_revoke_automatically.length}</Badge>
              </h4>
              {plan.will_revoke_automatically.length === 0 ? (
                <p className="admin-muted">
                  Nothing here is proven to have come from this enrolment, so nothing goes
                  automatically.
                </p>
              ) : (
                <ul className="ops-results">
                  {plan.will_revoke_automatically.map((key) => (
                    <li key={`auto:${key}`}>
                      <code>{key}</code> <Badge tone="success">Proven</Badge>
                    </li>
                  ))}
                </ul>
              )}

              {requiresConfirmation.length > 0 && (
                <section className="ops-confirm">
                  <h4>
                    Confirm each of these by name <Badge tone="warning">{requiresConfirmation.length}</Badge>
                  </h4>
                  <p className="admin-muted">{OPS_COPY.confirmEachUnproven}</p>
                  {requiresConfirmation.map((key) => (
                    <label className="ops-choice" key={`confirm:${key}`}>
                      <input
                        type="checkbox"
                        // UNTICKED BY DEFAULT and never pre-ticked from a count or a
                        // "select all": the whole point of this list is that the server could
                        // not prove these belong to this enrolment.
                        checked={confirmed[key] === true}
                        disabled={cancel.isPending}
                        onChange={(event) =>
                          setConfirmed({ ...confirmed, [key]: event.target.checked })
                        }
                      />
                      <span>
                        <code>{key}</code>
                      </span>
                    </label>
                  ))}
                </section>
              )}

              {plan.retained.length > 0 && (
                <>
                  <h4>Not touched</h4>
                  <ul className="ops-results">
                    {/* TWO keys on the preview's `retained[]`. No `decision` field here — do not
                        reach for one, and do not merge this with the cancel result's shape. */}
                    {plan.retained.map((row) => (
                      <li key={`retained:${row.target_key}`}>
                        <code>{row.target_key}</code>{' '}
                        <small className="admin-muted">{row.reason}</small>
                      </li>
                    ))}
                  </ul>
                </>
              )}

              <CertificateChoices
                offers={offers}
                value={choices}
                onChange={setChoices}
                disabled={cancel.isPending}
              />

              <ReasonField
                value={reason}
                onChange={setReason}
                showProblem={attempted}
                disabled={cancel.isPending}
              />
              <p className="admin-muted">{OPS_COPY.auditIsPermanent}</p>
            </>
          )}
        </>
      )}

      {cancel.isError &&
        (staleConfirmation ? (
          <Banner tone="danger">
            <p>{explainOpsFailure(cancel.error).message}</p>
            <p>
              The academy would not classify something you confirmed as removable, which means this
              preview is out of date — the learner&rsquo;s access changed after it was drawn.
              Nothing has been removed. Run the preview again and look at the current plan.
            </p>
          </Banner>
        ) : (
          <OpsFailureNotice error={cancel.error} onReload={rerunPreview} />
        ))}
    </Dialog>
  );
}
